// src/views/admin/clients/index.jsx
import { useToast } from '@chakra-ui/react';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import { columnsDataClients } from './variables/columnsDataServicesList.js';
import React, { useEffect, useState, useRef } from 'react';
import { getData, postData, putData } from '../../../utils/httpClient.js';
import authService from 'services/authService';
import { useSearch } from '../../../contexts/SearchContext.js';
import WarrantyTable from '../../../components/warranty/WarrantyTable.js';
import CampaignValidationModal from '../../../components/Appointment/CampaignValidationModal.jsx';
import { useResetPageOnFilterChange } from '../../../hooks/useResetPageOnFilterChange.js';
import { useTableSorting } from '../../../hooks/useTableSorting.js';
import {
  isWarrantyDiagnosisAgenda,
  isWarrantyRepairAgendaItem,
  resolveWarrantyExpiration,
} from '../../../utils/warrantyTypes.js';

export default function Settings() {
  const { searchQuery, resetListFilters, startDate, endDate } = useSearch(); 
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [, setModalState] = useState({ isOpen: false, provider: null, isNew: false });
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [vehicleCampaigns, setVehicleCampaigns] = useState([]);
  const [selectedVehicleChasis, setSelectedVehicleChasis] = useState('');
  const warrantyTableRef = useRef();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const toast = useToast();
  const { sorting, setSorting, sortBy, sortDir, appendSortParams } = useTableSorting();

  useResetPageOnFilterChange(setPage, page, [searchQuery, startDate, endDate, rowsPerPage, sortBy, sortDir]);

  const loadData = async (pageOverride = page) => {
    try {
      const queryParams = new URLSearchParams({
        page: pageOverride + 1,
        limit: rowsPerPage,
        data: searchQuery || '',
        startDate: startDate || '',
        endDate: endDate || '',
      });
      appendSortParams(queryParams);

      const response = await getData(`/vehicleServices/warranties?${queryParams.toString()}`);
      setData(response?.payload?.vehicleServices || []);
      setTotalPages(Math.ceil(response?.payload?.total / rowsPerPage));
    } catch (error) {
      toast({
        title: 'Error al obtener servicios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadData(page);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, startDate, endDate, rowsPerPage, sortBy, sortDir]);

  useEffect(() => {
    resetListFilters({ resetDates: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateVehicleCampaigns = async (chassisNumber) => {
    try {
      const response = await getData(`/campaigns/vehicle/${chassisNumber}`);
      
      if (response.payload && response.payload.campaigns.length > 0) {
        setVehicleCampaigns(response.payload.campaigns);
        setSelectedVehicleChasis(chassisNumber);
        
        // Si hay campañas sin revisar, mostrar el modal
        if (response.payload.unreviewedCount > 0) {
          setShowCampaignModal(true);
          return true; // Indica que hay campañas sin revisar
        }
      }
      return false; // No hay campañas sin revisar
    } catch (error) {
      // No mostrar error al usuario ya que no es crítico para el flujo
      console.log('Error al validar campañas:', error);
      return false;
    }
  };

  const handleCampaignModalClose = () => {
    setShowCampaignModal(false);
    // Notificar a la tabla para que abra el modal de edición con el servicio ya seleccionado
    if (warrantyTableRef.current) {
      warrantyTableRef.current.openEditModalDirectly();
    }
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false, provider: null, isNew: false });
  };

  const handleCreateWarranty = async (service) => {
    try {
      const newWarranty = {
        customerId: service.customerId,
        vehicleId: service.vehicleId,
        serviceId: service.id,
        warrantyType: service.warrantyType || '110',
        status: 'PENDIENTE',
        date: null,
        warrantyExpiration:
          resolveWarrantyExpiration(null, service) || null,
        stoppedVehicle: null,
        atApPosition: null,
        faultCode: null,
        manufacturerCode: null,
        authorizationRequestSent: null,
        claimSent: null,
        diss: null,
        tpi: null,
        saga: null,
        claim: null,
        authorization: null,
      };

      await postData('/warranty', newWarranty);
      loadData(page);
      closeEditModal(); 
    } catch (error) {
      toast({
        title: 'Error al crear garantía',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateWarranty = async (
    warranty,
    repairData = null,
    serviceStatusUpdate = null,
  ) => {
    try {
      const toWarrantyPayload = (w, overrides = {}) => ({
        id: w.id,
        customerId: w.customerId,
        vehicleId: w.vehicleId,
        serviceId: w.serviceId,
        warrantyType: w.warrantyType || null,
        status: w.status || 'PENDIENTE',
        repairAgendaId: w.repairAgendaId || null,
        date: w.date || null,
        warrantyExpiration: w.warrantyExpiration || null,
        stoppedVehicle: w.stoppedVehicle,
        atApPosition: w.atApPosition || null,
        faultCode: w.faultCode || null,
        manufacturerCode: w.manufacturerCode || null,
        authorizationRequestSent: w.authorizationRequestSent || null,
        claimSent: w.claimSent || null,
        diss: w.diss || null,
        tpi: w.tpi || null,
        saga: w.saga || null,
        claim: w.claim || null,
        authorization: w.authorization || null,
        warrantyProducts: w.warrantyProducts || [],
        customerObservations: w.customerObservations,
        mechanicObservation: w.mechanicObservation,
        ...overrides,
      });

      let savedWarranty = warranty;

      if (serviceStatusUpdate?.serviceId && serviceStatusUpdate?.statusId) {
        try {
          await putData(`/vehicleServices/${serviceStatusUpdate.serviceId}`, {
            id: serviceStatusUpdate.serviceId,
            statusId: serviceStatusUpdate.statusId,
          });
        } catch (serviceStatusError) {
          console.error(
            'Error al actualizar estado del servicio:',
            serviceStatusError,
          );
          toast({
            title: 'No se pudo cambiar el estado en agenda',
            description:
              serviceStatusError?.response?.data?.message ||
              'Revisá el estado e intentá nuevamente',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }

      if (warranty.id) {
        const response = await putData(
          `/warranty/${warranty.id}`,
          toWarrantyPayload(warranty),
        );
        savedWarranty = response?.payload?.warranty || warranty;
      } else {
        const response = await postData('/warranty', toWarrantyPayload(warranty));
        savedWarranty = response?.payload?.warranty || warranty;
      }

      const updateAgendaStatus = async (repairAgendaId, status) => {
        const agendaResponse = await getData(`/agenda/${repairAgendaId}`);
        const currentAgenda =
          agendaResponse?.payload?.agenda ??
          (agendaResponse?.id ? agendaResponse : null);
        if (!currentAgenda?.id) return;
        const {
          customer,
          vehicle,
          service,
          mechanic,
          serviceType,
          customerTransfer,
          vehicleService,
          ...agendaPayload
        } = currentAgenda;
        await putData(`/agenda/${currentAgenda.id}`, {
          ...agendaPayload,
          status,
        });
      };

      if (repairData?.statusOnly && repairData.repairAgendaId) {
        try {
          await updateAgendaStatus(repairData.repairAgendaId, repairData.status);
        } catch (repairStatusError) {
          console.error('Error al actualizar estado de reparación:', repairStatusError);
          toast({
            title: 'Garantía guardada, pero falló el estado de reparación',
            description:
              repairStatusError?.response?.data?.message ||
              'Revisá el estado e intentá nuevamente',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      } else if (repairData?.date && repairData?.time) {
        try {
          if (!Number(repairData.estimatedTime)) {
            repairData = { ...repairData, estimatedTime: 30 };
            toast({
              title: 'Duración en 0',
              description:
                'La reparación se agendó con una duración por defecto de 30 min. Podés ajustarla luego desde la agenda.',
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
          }
          if (!repairData.customerTransferId) {
            repairData = { ...repairData, customerTransferId: 1 };
          }
          const repairPetition = repairData.customerObservations || null;
          const { customerObservations: _repairPetition, ...agendaFields } = repairData;
          const saveRepairPetition = async (vehicleServiceId) => {
            if (!vehicleServiceId) return;
            await putData(`/vehicleServices/${vehicleServiceId}`, {
              id: vehicleServiceId,
              customerObservations: repairPetition,
            });
          };
          const lockedStatuses = ['FINALIZADA', 'NO GARANTÍA', 'Cancelado'];
          const nextWarrantyStatus = lockedStatuses.includes(savedWarranty.status)
            ? savedWarranty.status
            : 'REP. AGENDADA';

          let agenda = null;
          const diagnosisServiceId = Number(
            savedWarranty.serviceId || warranty.serviceId,
          );
          let hadValidRepairAgendaLink = Boolean(savedWarranty.repairAgendaId);

          const repairDay = String(repairData.date).slice(0, 10);
          const repairTime = String(repairData.time).slice(0, 5);

          const createRepairAgenda = async () => {
            const agendaResponse = await postData('/agenda', {
              ...agendaFields,
              status: repairData.status || 'Pendiente',
              allowOverlap: true,
            });
            return agendaResponse?.payload?.agenda || null;
          };

          if (savedWarranty.repairAgendaId) {
            const agendaResponse = await getData(
              `/agenda/${savedWarranty.repairAgendaId}`,
            );
            const currentAgenda =
              agendaResponse?.payload?.agenda ??
              (agendaResponse?.id ? agendaResponse : null);
            if (
              currentAgenda?.id &&
              isWarrantyDiagnosisAgenda(currentAgenda, diagnosisServiceId)
            ) {
              hadValidRepairAgendaLink = false;
            } else if (currentAgenda?.id) {
              const {
                customer,
                vehicle,
                service,
                mechanic,
                serviceType,
                customerTransfer,
                vehicleService,
                ...agendaPayload
              } = currentAgenda;
              await putData(`/agenda/${currentAgenda.id}`, {
                ...agendaPayload,
                ...agendaFields,
                id: currentAgenda.id,
                status: repairData.status || 'Pendiente',
              });
              agenda = { ...currentAgenda, id: currentAgenda.id };
              await saveRepairPetition(currentAgenda.vehicleServiceId);
            }
          }

          if (!agenda?.id) {
            try {
              const dayAgenda = await getData(
                `/agenda?from=${repairDay}&to=${repairDay}`,
              );
              const sameRepairSlot = (
                dayAgenda?.payload?.agendas || []
              ).find((item) => {
                if (String(item.status || '').toLowerCase() === 'cancelado') {
                  return false;
                }
                if (Number(item.vehicleId) !== Number(repairData.vehicleId)) {
                  return false;
                }
                if (String(item.time || '').slice(0, 5) !== repairTime) {
                  return false;
                }
                return isWarrantyRepairAgendaItem(item);
              });
              if (sameRepairSlot?.id) agenda = sameRepairSlot;
            } catch (lookupError) {
              console.error('No se pudo buscar agenda existente:', lookupError);
            }
          }

          if (!agenda?.id) {
            agenda = await createRepairAgenda();
          }

          if (
            agenda?.id &&
            isWarrantyDiagnosisAgenda(agenda, diagnosisServiceId)
          ) {
            agenda = await createRepairAgenda();
            hadValidRepairAgendaLink = false;
          }

          if (agenda?.id && !hadValidRepairAgendaLink) {
            if (!agenda.vehicleServiceId) {
            const vsResponse = await postData('/vehicleServices', {
              customerId: repairData.customerId,
              vehicleId: repairData.vehicleId,
              mechanicId: repairData.mechanicId,
              invoiceId: null,
              creatorUserId: authService.getCurrentUser()?.id || null,
              description: repairData.description || 'Reparación de garantía',
              observations: null,
              customerObservations: repairPetition,
              mechanicObservation: null,
              customerTransferId: repairData.customerTransferId || 1,
              canceledObservation: null,
              date: repairData.date,
              time: repairData.time,
              statusId: 1,
              serviceTypeId: repairData.serviceTypeId || 3,
              createdAt: new Date(),
              updatedAt: new Date(),
              serviceName: repairData.description || 'Reparación de garantía',
              volkswagenCost: null,
              warrantyType: null,
              totalTime: Number(repairData.estimatedTime) || 0,
              serviceStart: null,
              serviceEnd: null,
              serviceId: null,
              vehicleServiceEventsHistories: [],
            });

            const vehicleService = vsResponse?.payload?.vehicleService;
            if (vehicleService?.id) {
              await putData(`/agenda/${agenda.id}`, {
                ...agenda,
                ...agendaFields,
                vehicleServiceId: vehicleService.id,
                status: repairData.status || 'Pendiente',
              });
            }
            } else if (
              agenda.vehicleServiceId &&
              Number(agenda.vehicleServiceId) !== diagnosisServiceId
            ) {
              await saveRepairPetition(agenda.vehicleServiceId);
            }

            if (!lockedStatuses.includes(savedWarranty.status)) {
              await putData(
                `/warranty/${savedWarranty.id}`,
                toWarrantyPayload(savedWarranty, {
                  repairAgendaId: agenda.id,
                  status: nextWarrantyStatus,
                }),
              );
              savedWarranty = {
                ...savedWarranty,
                repairAgendaId: agenda.id,
                status: nextWarrantyStatus,
              };
            }
          } else if (agenda?.id && hadValidRepairAgendaLink) {
            if (!lockedStatuses.includes(savedWarranty.status)) {
              await putData(
                `/warranty/${savedWarranty.id}`,
                toWarrantyPayload(savedWarranty, {
                  status: nextWarrantyStatus,
                }),
              );
              savedWarranty = {
                ...savedWarranty,
                status: nextWarrantyStatus,
              };
            }
          }

          toast({
            title: savedWarranty.repairAgendaId ? 'Reparación actualizada' : 'Reparación agendada',
            description: `Reparación programada para el ${repairData.date} a las ${repairData.time}`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } catch (agendaError) {
          console.error('Error al crear la agenda:', agendaError);
          toast({
            title: 'Garantía guardada, pero error al agendar reparación',
            description:
              agendaError?.response?.data?.message ||
              agendaError?.message ||
              'La garantía se guardó correctamente pero hubo un problema al crear la agenda',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }

      const serviceId = savedWarranty.serviceId || warranty.serviceId;
      if (savedWarranty.status === 'FINALIZADA' && serviceId) {
        try {
          await postData(`/vehicleServices/${serviceId}/warranty-pdf`, {});
        } catch (pdfError) {
          console.error('Error al generar registro de garantía:', pdfError);
        }
      }

      setPage(0);
      await loadData(0);
      closeEditModal();
      toast({
        title: warranty.id ? 'Garantía actualizada' : 'Garantía creada',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error al guardar garantía:', error);
      toast({
        title: `Error al ${warranty.id ? 'actualizar' : 'crear'} la garantía`,
        description: error.response?.data?.message || 'Intenta nuevamente',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <AdminPageShell
      title="Garantías"
      
    >
      <WarrantyTable
          ref={warrantyTableRef}
          columnsData={columnsDataClients}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          pageSize={rowsPerPage}
          setPageSize={setRowsPerPage}
          setData={setData}
          handleUpdateWarranty={handleUpdateWarranty}
          handleCreateWarranty={handleCreateWarranty}
          onValidateCampaigns={validateVehicleCampaigns}
          sorting={sorting}
          onSortingChange={setSorting}
        />

      
      <CampaignValidationModal
        isOpen={showCampaignModal}
        onClose={handleCampaignModalClose}
        campaigns={vehicleCampaigns}
        chassisNumber={selectedVehicleChasis}
      />
    </AdminPageShell>
  );
}
